import {
  CORRECT_NBN_ORDER_REQUEST_TYPE,
  HIDDEN_AUDIT_HISTORY_FIELDS,
  HIDDEN_AUDIT_HISTORY_REQUEST_TYPES,
  NBN_ORDER_CORRECTION_FIELDS
} from "@/constants/nbn-order-correction-constants";
import {
  getBandwidthProfilesByProductType,
  getFTTCBandwidthProfiles,
  NBN_ASSURE_ALLOWED_SPEED
} from "@/constants/nbn-edit-form-constants";
import { getNbnProductType } from "@/utils/mappings";

function isPresent(value) {
  return value !== undefined && value !== null;
}

export function normalizeCorrectionValue(value) {
  if (value === undefined || value === null) {
    return "";
  }

  return String(value);
}

export function getCorrectionFieldValue(order, field) {
  if (!order || !field) {
    return "";
  }

  const aliases = field.aliases || [];

  for (let i = 0; i < aliases.length; i++) {
    const alias = aliases[i];
    if (Object.prototype.hasOwnProperty.call(order, alias) && isPresent(order[alias])) {
      return normalizeCorrectionValue(order[alias]);
    }
  }

  if (Object.prototype.hasOwnProperty.call(order, field.key) && isPresent(order[field.key])) {
    return normalizeCorrectionValue(order[field.key]);
  }

  return "";
}

export function buildCorrectionDraft(order) {
  return NBN_ORDER_CORRECTION_FIELDS.reduce((draft, field) => {
    draft[field.key] = getCorrectionFieldValue(order, field);
    return draft;
  }, {});
}

export function isCorrectionFieldReadOnly(field) {
  return !!(field && field.readOnly);
}

export function getCorrectionProductType(product) {
  return getNbnProductType(product) || product || "";
}

function parseOrderRequest(order) {
  if (!order || order.request == null) {
    return null;
  }

  if (typeof order.request === "object") {
    return order.request;
  }

  if (typeof order.request !== "string") {
    return null;
  }

  try {
    return JSON.parse(order.request);
  } catch (e) {
    return null;
  }
}

function hasNbnAssure(source) {
  if (!source) {
    return false;
  }

  const required =
    source.nbnAssureRequired ?? source.NbnAssureRequired;
  if (
    required === true ||
    String(required).toLowerCase() === "yes" ||
    String(required).toLowerCase() === "true"
  ) {
    return true;
  }

  const option =
    source.nbnAssureOption ?? source.nbnAssure ?? source.NbnAssure;
  if (option && String(option).toLowerCase() !== "no") {
    return true;
  }

  return false;
}

export function isNbnAssureSelected(order, mappedOrder) {
  if (hasNbnAssure(order) || hasNbnAssure(parseOrderRequest(order))) {
    return true;
  }

  if (
    mappedOrder &&
    order &&
    mappedOrder.id &&
    order.id &&
    String(mappedOrder.id) === String(order.id)
  ) {
    return hasNbnAssure(mappedOrder);
  }

  return false;
}

function readNbnAssureOption(source) {
  if (!source) {
    return "";
  }

  const option =
    source.nbnAssureOption ?? source.nbnAssure ?? source.NbnAssure;
  if (!option || String(option).toLowerCase() === "no") {
    return "";
  }

  return String(option);
}

export function getNbnAssureDisplayValue(order, mappedOrder) {
  if (!isNbnAssureSelected(order, mappedOrder)) {
    return "";
  }

  const option =
    readNbnAssureOption(order) ||
    readNbnAssureOption(parseOrderRequest(order)) ||
    (mappedOrder &&
    order &&
    mappedOrder.id &&
    order.id &&
    String(mappedOrder.id) === String(order.id)
      ? readNbnAssureOption(mappedOrder)
      : "");

  return option ? `Yes (${option})` : "Yes";
}

export function getCorrectionBandwidthProfiles(
  product,
  isSog6028Enabled,
  isAssureSelected
) {
  const productType = getCorrectionProductType(product);
  const options =
    productType === "FTTC"
      ? getFTTCBandwidthProfiles(isSog6028Enabled)
      : getBandwidthProfilesByProductType(productType);

  if (!isAssureSelected) {
    return options;
  }

  return options.filter(item => NBN_ASSURE_ALLOWED_SPEED.includes(item.text));
}

export function getCorrectionBandwidthLabel(storedValue, product, isSog6028Enabled) {
  const value = normalizeCorrectionValue(storedValue);
  if (!value) {
    return "";
  }

  const options = getCorrectionBandwidthProfiles(product, isSog6028Enabled);
  const exactValue = options.find(item => item.value === value);
  if (exactValue) {
    return exactValue.text;
  }

  const exactText = options.find(item => item.text === value);
  if (exactText) {
    return exactText.text;
  }

  const partial = options.find(
    item => item.text.includes(value) || value.includes(item.text)
  );
  if (partial) {
    return partial.text;
  }

  return value;
}

export function getCorrectionChanges(currentValues, nextValues) {
  return NBN_ORDER_CORRECTION_FIELDS.reduce((changes, field) => {
    if (isCorrectionFieldReadOnly(field)) {
      return changes;
    }

    const currentValue = normalizeCorrectionValue(
      currentValues ? currentValues[field.key] : ""
    );
    const nextValue = normalizeCorrectionValue(
      nextValues ? nextValues[field.key] : ""
    );

    if (currentValue !== nextValue) {
      changes.push({
        field: field.key,
        label: field.label,
        oldValue: currentValue,
        newValue: nextValue
      });
    }

    return changes;
  }, []);
}

export function buildCorrectNbnOrderPayload(order, currentValues, nextValues, reason) {
  const changes = getCorrectionChanges(currentValues, nextValues);
  const oldValues = {};
  const newValues = {};

  changes.forEach(change => {
    oldValues[change.field] = change.oldValue;
    newValues[change.field] = change.newValue;
  });

  const payload = {
    id: order ? order.id : null,
    reason: reason ? String(reason).trim() : "",
    source: CORRECT_NBN_ORDER_REQUEST_TYPE,
    oldValues,
    newValues
  };

  changes.forEach(change => {
    payload[change.field] = change.newValue;
  });

  if (!Object.prototype.hasOwnProperty.call(newValues, "NbnOrderId")) {
    payload.nbnOrderId = order ? order.nbnOrderId || order.NbnOrderId || "" : "";
  }

  if (!Object.prototype.hasOwnProperty.call(newValues, "AvcId")) {
    payload.avcId = order ? order.avcId || order.AvcId || "" : "";
  }

  return payload;
}

export function applyCorrectionValues(order, newValues) {
  if (!order || !newValues) {
    return order;
  }

  const nextOrder = Object.assign({}, order);

  NBN_ORDER_CORRECTION_FIELDS.forEach(field => {
    if (isCorrectionFieldReadOnly(field)) {
      return;
    }

    if (!Object.prototype.hasOwnProperty.call(newValues, field.key)) {
      return;
    }

    const value = newValues[field.key];
    nextOrder[field.key] = value;
    (field.aliases || []).forEach(alias => {
      nextOrder[alias] = value;
    });
  });

  return nextOrder;
}

export function filterAuditLogsForOrder(logs, order) {
  if (!Array.isArray(logs) || !order) {
    return [];
  }

  const nbnOrderId = normalizeCorrectionValue(
    order.nbnOrderId || order.NbnOrderId || order.nbnOrderID
  );
  const avcId = normalizeCorrectionValue(order.avcId || order.AvcId);

  return logs.filter(log => {
    const logOrderId = normalizeCorrectionValue(log.nbnOrderID || log.nbnOrderId);
    const logAvcId = normalizeCorrectionValue(log.avcId);

    return (
      (nbnOrderId && logOrderId === nbnOrderId) ||
      (avcId && logAvcId === avcId)
    );
  });
}

export function normalizeAuditHistoryKey(value) {
  return String(value || "")
    .replace(/[_\s-]/g, "")
    .toLowerCase();
}

export function isHiddenAuditHistoryField(field) {
  return HIDDEN_AUDIT_HISTORY_FIELDS.indexOf(normalizeAuditHistoryKey(field)) !== -1;
}

export function isHiddenAuditHistoryRequestType(requestType) {
  return (
    HIDDEN_AUDIT_HISTORY_REQUEST_TYPES.indexOf(
      normalizeAuditHistoryKey(requestType)
    ) !== -1
  );
}

export function parseAuditPayload(requestPayload) {
  if (!requestPayload) {
    return null;
  }

  try {
    const parsed =
      typeof requestPayload === "string"
        ? JSON.parse(requestPayload)
        : requestPayload;

    if (!parsed) {
      return null;
    }

    if (Array.isArray(parsed.changes) && parsed.changes.length) {
      const oldValues = {};
      const newValues = {};

      parsed.changes.forEach(change => {
        if (!change || !change.field) {
          return;
        }

        oldValues[change.field] =
          change.previousValue !== undefined ? change.previousValue : change.oldValue;
        newValues[change.field] = change.newValue;
      });

      return {
        oldValues,
        newValues,
        reason: parsed.reason || ""
      };
    }

    if (!parsed.oldValues || !parsed.newValues) {
      return null;
    }

    const oldValues =
      typeof parsed.oldValues === "string"
        ? JSON.parse(parsed.oldValues)
        : parsed.oldValues;
    const newValues =
      typeof parsed.newValues === "string"
        ? JSON.parse(parsed.newValues)
        : parsed.newValues;

    return {
      oldValues,
      newValues,
      reason: parsed.reason || ""
    };
  } catch (e) {
    return null;
  }
}

export function flattenAuditEntries(logs) {
  const entries = [];

  (logs || []).forEach(log => {
    if (isHiddenAuditHistoryRequestType(log.requestType)) {
      return;
    }

    const parsed = parseAuditPayload(log.requestPayload);
    if (!parsed) {
      if (isHiddenAuditHistoryField(log.requestType)) {
        return;
      }

      entries.push({
        field: log.requestType || "Request",
        oldValue: "",
        newValue: log.requestPayload,
        userEmail: log.userEmail,
        dateTime: log.dateTime,
        reason: ""
      });
      return;
    }

    Object.keys(parsed.newValues || {}).forEach(field => {
      if (isHiddenAuditHistoryField(field)) {
        return;
      }

      entries.push({
        field,
        oldValue: parsed.oldValues[field],
        newValue: parsed.newValues[field],
        userEmail: log.userEmail,
        dateTime: log.dateTime,
        reason: parsed.reason
      });
    });
  });

  return entries;
}

export function isCorrectionAuditRequest(requestType) {
  return (
    requestType === CORRECT_NBN_ORDER_REQUEST_TYPE ||
    requestType === "Update NBN Order"
  );
}

export function isCorrectionActionAllowed({
  isSuperAdmin,
  isOriginal,
  recordId
}) {
  return Boolean(isSuperAdmin && isOriginal && recordId);
}

const CORRECTION_DISABLED_STATUSES = [
  "rejected",
  "submitted",
  "inprogress",
  "in-progress",
  "in progress"
];

export function isCorrectionActionDisabled(status) {
  const normalized = String(status || "").trim().toLowerCase();
  return CORRECTION_DISABLED_STATUSES.includes(normalized);
}

export function isCorrectionReasonProvided(reason) {
  return Boolean(String(reason || "").trim());
}

export function buildCorrectionAuditLog(order, payload, userEmail) {
  const nbnOrderId = order
    ? order.nbnOrderId || order.NbnOrderId || order.nbnOrderID || ""
    : "";
  const avcId = order ? order.avcId || order.AvcId || "" : "";

  return {
    nbnOrderID: nbnOrderId,
    nbnOrderId: nbnOrderId,
    avcId,
    requestType: CORRECT_NBN_ORDER_REQUEST_TYPE,
    requestPayload: JSON.stringify({
      oldValues: payload && payload.oldValues ? payload.oldValues : {},
      newValues: payload && payload.newValues ? payload.newValues : {},
      reason: payload && payload.reason ? payload.reason : ""
    }),
    userEmail: userEmail || "",
    dateTime: new Date().toISOString()
  };
}

export function getWholesalerDisplayName(wholesaler) {
  if (!wholesaler) {
    return "";
  }

  return String(
    wholesaler.company ||
      wholesaler.name ||
      wholesaler.wholesaler ||
      wholesaler.Company ||
      ""
  ).trim();
}

export function findWholesalerByAccount(wholesalers, accountNumber) {
  if (accountNumber === undefined || accountNumber === null || accountNumber === "") {
    return null;
  }

  return (wholesalers || []).find(
    item => String(item.accountNumber) === String(accountNumber)
  ) || null;
}

export function applyWholesalerSelection(wholesalers, accountNumber) {
  const selected = findWholesalerByAccount(wholesalers, accountNumber);

  return {
    Wholesaler: selected ? getWholesalerDisplayName(selected) : "",
    WholesalerAccountNumber:
      accountNumber === undefined || accountNumber === null || accountNumber === ""
        ? ""
        : String(accountNumber)
  };
}
